import { ReactNode, useState } from 'react';
import { ArrowBackIcon } from '@chakra-ui/icons';
import WeeklyReportPreview from './WeeklyReportPreview';
import { Box, Button, Center, Flex, Image, Text, VStack } from '@chakra-ui/react';
import useWeeklyReportPeriod from '@/hooks/peersupport/useWeeklyReportPeriod';
import useSubmitWeeklyReport from '@/hooks/peersupport/useSubmitWeeklyReport';
import NumericChip from '@/components/atoms/NumericChip';
import WeeklyReportContainer from './WeeklyReportContainer';
import WeeklyReportConfirmation from './WeeklyReportConfirmation';
import WeeklyReportPageSection from './WeeklyReportPageSection';
import WeeklyReportAssignmentBadge from './WeeklyReportAssignmentBadge';
import {
  WELLNESS_DOMAINS_DISPLAY_LABELS,
  WELLNESS_SUBDOMAINS_PER_DOMAIN,
  WELLNESS_SUB_DOMAINS_DISPLAY_LABELS,
  PEER_SUPPORT_EVENT_OUTCOME,
  PEER_SUPPORT_EVENT_OUTCOME_DISPLAY_LABELS,
} from '@/constants';
import { THealthDomain } from '@/types/Health.types';
import { WeeklyReportDraft, excessAssignments, isValidReport, setCount, total } from './model/types';
import { background, extra, primary, secondary, text } from '@/theme/colors/palettes';
import getDomainIconFromDomain from '@/utils/getDomainIconFromDomain';
import { APP_NAME } from '@/constants/stringVars';
import LogoSpinner from '@/components/atoms/LogoSpinner';
import WeeklyReportDashboardButton from './WeeklyReportDashboardButton';

const domains: THealthDomain[] = ['mental', 'physical', 'social', 'spiritual', 'vocational', 'fun', 'purpose'];
const topicLabel = (topic: string) =>
  topic.startsWith('OTHER_') ? 'Other' : (WELLNESS_SUB_DOMAINS_DISPLAY_LABELS[topic] ?? topic);

const NoticeBox = ({ title, children }: { title: string; children: ReactNode }) => {
  return (
    <Box
      bg={extra.white}
      p={{ base: 6, md: 8 }}
      borderRadius="16px"
      border={`1px solid ${extra.mediumGray30pct}`}
      boxShadow="0px 2px 6px 0px #0041791A"
      role="status"
    >
      <Text variant="urbanistBold16" mb={3}>
        {title}
      </Text>
      <Text>{children}</Text>
      <WeeklyReportDashboardButton mt={4}>Back to Dashboard</WeeklyReportDashboardButton>
    </Box>
  );
};

export function WeeklyReportAccountMismatch() {
  return (
    <VStack spacing="25.6px" textAlign="center" role="status">
      <Text color={text.mediumBlue} fontSize="18px">
        You are logged in as another user. Log in with the email that you received the link to.
      </Text>
      <WeeklyReportDashboardButton accountMismatch />
    </VStack>
  );
}

export function WeeklyReportError({
  error,
  retry,
  submitting = false,
}: {
  error: unknown;
  retry: () => void;
  submitting?: boolean;
}) {
  const reportError = error as { code?: number; message?: unknown; errors?: { message?: unknown }[] } | null;
  const code = reportError?.code;
  const backendMessage = reportError?.errors?.[0]?.message ?? reportError?.message;
  const message = typeof backendMessage === 'string' && backendMessage.trim() ? backendMessage : undefined;
  if (code === 404)
    return (
      <NoticeBox title="This weekly report could not be found">
        {message ?? 'Please open the link in your latest weekly reminder.'}
      </NoticeBox>
    );
  if (code === 410)
    return (
      <NoticeBox title="This report link has expired">
        {message ?? 'Please open the latest weekly reminder to report your contacts.'}
      </NoticeBox>
    );
  if (code === 409) return <WeeklyReportConfirmation />;
  if (code === 403) return <WeeklyReportAccountMismatch />;
  return (
    <Center role="status">
      <VStack>
        <Text mb={4}>
          {message ??
            (submitting
              ? 'We couldn’t submit your report. Your counts are still here. Please try again.'
              : 'We couldn’t load your weekly report. Please try again.')}
        </Text>
        <Button
          onClick={retry}
          fontWeight={600}
          boxShadow="0px 2px 6px 0px #0041791A"
          _hover={{ backgroundColor: background.lightGray }}
        >
          Reload
        </Button>
      </VStack>
    </Center>
  );
}

const Chips = ({ children }: { children: ReactNode }) => (
  <Flex wrap="wrap" gap={2}>
    {children}
  </Flex>
);

const WeeklyReportPage = ({ period, token }: { period?: string; token?: string }) => {
  const { data, isPending, isError, error, refetch } = useWeeklyReportPeriod(period, token);

  const [draft, setDraft] = useState<WeeklyReportDraft>({ contactCount: 0, domains: {}, topics: {}, outcomes: {} });
  const [preview, setPreview] = useState(false);
  const [loadedPeriod, setLoadedPeriod] = useState<string>();

  if (data && loadedPeriod !== data.periodStart) {
    setLoadedPeriod(data.periodStart);
    setDraft(data.draft);
    setPreview(false);
  }

  const {
    mutate,
    reset,
    isSuccess: isSubmitSuccess,
    isError: isSubmitError,
    error: submitError,
    isPending: isSubmitting,
  } = useSubmitWeeklyReport(data?.periodStart, draft, token);

  const changeDomain = (domain: string, count: number) =>
    setDraft((prev) => ({ ...prev, domains: setCount(prev.domains, domain, count) }));

  const changeContacts = (count: number) => setDraft((prev) => ({ ...prev, contactCount: count }));

  const excessDomains = excessAssignments(draft.domains, draft.contactCount);
  // Keep existing selections visible when contacts are reduced so they can be removed explicitly.
  const showReportSections =
    draft.contactCount > 0 ||
    total(draft.domains) > 0 ||
    Object.values(draft.topics).some((topics) => total(topics) > 0) ||
    total(draft.outcomes) > 0;

  let pageContent: ReactNode;

  if (isPending)
    pageContent = (
      <VStack gap={6}>
        <Text role="status" textAlign={'center'}>
          Loading your weekly report…
        </Text>
        <LogoSpinner size="40" />
      </VStack>
    );
  else if (isError) pageContent = <WeeklyReportError error={error} retry={() => void refetch()} />;
  else if (data.status === 'expired')
    pageContent = (
      <NoticeBox title="This reporting period has expired">
        Please open your latest reminder to complete your weekly report.
      </NoticeBox>
    );
  else if (isSubmitSuccess) pageContent = <WeeklyReportConfirmation variant="success" />;
  else if (data.status === 'already-submitted' || (isSubmitError && (submitError as { code?: number })?.code === 409))
    pageContent = <WeeklyReportConfirmation submittedAt={data.submittedAt} />;
  else
    pageContent = (
      <>
        {preview ? (
          <WeeklyReportPreview
            draft={draft}
            existingContactCount={data.existingContactCount}
            domains={domains}
            topicLabel={topicLabel}
          />
        ) : (
          <>
            {data.existingContactCount > 0 && (
              <Box
                bg={extra.white}
                border={`1px solid ${extra.mediumBlue20pct}`}
                borderRadius="12px"
                p={5}
                boxShadow={`0px 2px 6px 0px ${extra.mediumBlue5pct}`}
                fontSize="16px"
                fontWeight={500}
              >
                You already logged{' '}
                <Box
                  as="span"
                  bg={secondary[500]}
                  color={extra.white}
                  borderRadius="full"
                  px={2}
                  py={1}
                  fontSize="12px"
                  lineHeight="14px"
                  fontWeight={900}
                >
                  {data.existingContactCount} contacts
                </Box>{' '}
                via {APP_NAME} Web or Mobile apps in last 7 days.
                <br />
                You shouldn’t include these here, they are already in the system.
              </Box>
            )}
            <Text variant="loraExtraSmallTitle" mt={10} textAlign={'start'}>
              Let’s log the support you provided this week
            </Text>
            <Text color="text.mediumGray" mt={1} fontSize={'14px'} fontWeight={600}>
              Think about the last 7 days, or since your previous report. It takes about a minute.
            </Text>

            <WeeklyReportPageSection
              title="Number of Contacts"
              description="How many supportive conversations did you have over the past 7 days?"
            >
              <Flex
                bg={extra.white}
                border={`1px solid ${extra.mediumGray30pct}`}
                borderRadius="8px"
                w="160px"
                h="53px"
                align="center"
                justify="space-between"
                boxShadow="sm"
              >
                <Button
                  variant="unstyled"
                  aria-label="Decrease number of contacts"
                  isDisabled={!draft.contactCount}
                  onClick={() => changeContacts(draft.contactCount - 1)}
                  width={'50px'}
                  display="inline-flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Image
                    src="/svg/buttonMinusBlue.svg"
                    alt="Decrease number of contacts"
                    objectFit="contain"
                    boxSize="18px"
                  />
                </Button>
                <Text fontWeight={800} fontSize="18px" lineHeight={'16px'} minWidth={'20px'} textAlign={'center'}>
                  {draft.contactCount}
                </Text>
                <Button
                  variant="unstyled"
                  aria-label="Increase number of contacts"
                  isDisabled={draft.contactCount >= Number.MAX_SAFE_INTEGER}
                  onClick={() => changeContacts(draft.contactCount + 1)}
                  width={'50px'}
                  display="inline-flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Image
                    src="/svg/buttonPlusBlue.svg"
                    alt="Increase number of contacts"
                    objectFit="contain"
                    boxSize="16px"
                  />
                </Button>
              </Flex>
            </WeeklyReportPageSection>

            {showReportSections && (
              <>
                <WeeklyReportPageSection
                  title="Wholeness Domains"
                  remaining={excessDomains ? -excessDomains : Math.max(0, draft.contactCount - total(draft.domains))}
                  selectedCount={total(draft.domains)}
                  description="Use +/− to show how many conversations covered each domain. Each contact must include at least one domain."
                >
                  <Chips>
                    {domains.map((domain) => (
                      <NumericChip
                        key={domain}
                        label={WELLNESS_DOMAINS_DISPLAY_LABELS[domain]}
                        count={draft.domains[domain]}
                        max={draft.contactCount}
                        onChange={(count) => changeDomain(domain, count)}
                      />
                    ))}
                  </Chips>
                </WeeklyReportPageSection>

                {(!!Object.keys(draft.domains).length || !!Object.keys(draft.topics).length) && (
                  <WeeklyReportPageSection
                    title="Topics Discussed"
                    description={
                      'For each selected domain, choose one or more topics discussed. Select “Other” if none of the options fit.'
                    }
                  >
                    {domains
                      .filter((domain) => draft.domains[domain] || total(draft.topics[domain] ?? {}) > 0)
                      .map((domain) => {
                        const DomainIcon = getDomainIconFromDomain(domain);
                        const selectedTopicCount = total(draft.topics[domain] ?? {});
                        const domainCount = draft.domains[domain] ?? 0;
                        const topicMax = Math.min(domainCount, draft.contactCount);
                        const excessTopics = excessAssignments(draft.topics[domain] ?? {}, topicMax);
                        const topicsComplete = excessTopics === 0 && selectedTopicCount >= domainCount;
                        return (
                          <Box key={domain} mt={4}>
                            <Flex gap={4} align="center" mb={4}>
                              <Flex align="center" gap={3}>
                                <Box aria-hidden="true" boxSize="26px" flexShrink={0}>
                                  <DomainIcon color={text.mediumBlue} size={26} />
                                </Box>
                                <Text variant="urbanistBold16" as="h3">
                                  {WELLNESS_DOMAINS_DISPLAY_LABELS[domain]}
                                </Text>
                              </Flex>
                              <WeeklyReportAssignmentBadge isComplete={topicsComplete}>
                                {excessTopics > 0
                                  ? `Over-assigned: Remove ${excessTopics}`
                                  : topicsComplete
                                    ? `${selectedTopicCount} Selected`
                                    : `Assign min. ${domainCount - selectedTopicCount}`}
                              </WeeklyReportAssignmentBadge>
                            </Flex>
                            <Chips>
                              {WELLNESS_SUBDOMAINS_PER_DOMAIN[domain].map((topic) => (
                                <NumericChip
                                  key={topic}
                                  label={topicLabel(topic)}
                                  count={draft.topics[domain]?.[topic]}
                                  max={topicMax}
                                  onChange={(count) =>
                                    setDraft((prev) => {
                                      const topics = { ...prev.topics };
                                      const updated = setCount(topics[domain] ?? {}, topic, count);
                                      if (Object.keys(updated).length) topics[domain] = updated;
                                      else delete topics[domain];
                                      return { ...prev, topics };
                                    })
                                  }
                                />
                              ))}
                            </Chips>
                          </Box>
                        );
                      })}
                  </WeeklyReportPageSection>
                )}

                <WeeklyReportPageSection
                  title="Conversation Outcome"
                  remaining={draft.contactCount - total(draft.outcomes)}
                  selectedCount={total(draft.outcomes)}
                  description="Select one outcome for each contact."
                >
                  <Chips>
                    {PEER_SUPPORT_EVENT_OUTCOME.map((outcome) => (
                      <NumericChip
                        key={outcome}
                        label={PEER_SUPPORT_EVENT_OUTCOME_DISPLAY_LABELS[outcome]}
                        count={draft.outcomes[outcome]}
                        max={Math.max(
                          draft.outcomes[outcome] ?? 0,
                          draft.contactCount - total(draft.outcomes) + (draft.outcomes[outcome] ?? 0),
                        )}
                        onChange={(count) =>
                          setDraft((prev) => ({ ...prev, outcomes: setCount(prev.outcomes, outcome, count) }))
                        }
                      />
                    ))}
                  </Chips>
                </WeeklyReportPageSection>
              </>
            )}
          </>
        )}
        {isSubmitError && (
          <Box mt={6}>
            <WeeklyReportError submitting error={submitError} retry={() => mutate()} />
          </Box>
        )}
        <Flex mt={10} direction="column" align="center" gap={3}>
          <Button
            bg={preview ? secondary[500] : primary[500]}
            color={extra.white}
            _hover={{ bg: preview ? secondary[600] : primary[600] }}
            _disabled={{ opacity: 0.48, cursor: 'not-allowed' }}
            w={'300px'}
            maxW="100%"
            h={'54px'}
            boxShadow="sm"
            isDisabled={!isValidReport(draft)}
            isLoading={isSubmitting}
            onClick={() => {
              if (!isValidReport(draft)) return;
              if (preview) mutate();
              else {
                setPreview(true);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }
            }}
          >
            {preview ? 'Looks good, submit!' : 'Preview'}
          </Button>
          {preview && (
            <Button
              variant="ghost"
              color={text.mediumBlue}
              leftIcon={<ArrowBackIcon stroke="currentColor" strokeWidth={1.5} marginBottom={0.5} />}
              mt={3}
              isDisabled={isSubmitting}
              onClick={() => {
                setPreview(false);
                reset();
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              fontWeight={700}
            >
              Edit Data
            </Button>
          )}
          {!preview && <WeeklyReportDashboardButton mt={showReportSections ? 4 : 0} isDisabled={isSubmitting} />}
        </Flex>
      </>
    );

  return <WeeklyReportContainer>{pageContent}</WeeklyReportContainer>;
};

export default WeeklyReportPage;
